import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AlertCircle, Home, ArrowLeft } from 'lucide-react';

/**
 * NotFound (404) Page
 * Displayed when a route doesn't match any known page.
 * Provides clear error message and navigation options.
 */
export const NotFound: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full rounded-2xl border border-border bg-card px-8 py-10 text-center shadow-sm">
        {/* Error Icon */}
        <div className="mb-6 flex justify-center">
          <div className="rounded-full border border-destructive/20 bg-destructive/10 p-4">
            <AlertCircle className="h-12 w-12 text-destructive" strokeWidth={1.5} />
          </div>
        </div>

        {/* Error Code */}
        <h1 className="mb-2 text-6xl font-bold text-foreground">404</h1>

        {/* Error Title */}
        <h2 className="mb-3 text-2xl font-semibold text-foreground">Page Not Found</h2>

        {/* Error Description */}
        <p className="mb-8 leading-relaxed text-muted-foreground">
          The page you're looking for doesn't exist or has been moved. Check the URL and try again.
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 sm:flex-row sm:gap-2 justify-center">
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate(-1)}
            className="flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </Button>
          <Button
            size="lg"
            onClick={() => navigate('/')}
            className="flex items-center justify-center gap-2"
          >
            <Home className="w-4 h-4" />
            Home
          </Button>
        </div>

        {/* Help Text */}
        <p className="mt-8 text-sm text-muted-foreground">
          Need help?{' '}
          <button
            onClick={() => navigate('/docs')}
            className="text-accent font-medium underline-offset-4 hover:underline transition-colors duration-200 cursor-pointer"
          >
            Visit Documentation
          </button>
        </p>
      </div>
    </div>
  );
};

export default NotFound;
